import {action, makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IRound} from "data/providers/json/json.provider";
import type {IUserStore} from "data/stores/user/user.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {ICrunchStore} from "data/stores/crunch/crunch.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RoundStatus} from "data/enums";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IGameBarResponse} from "data/types/api";

export interface IGameBarController extends ViewController {
	roundChange: (roundId: IRound) => void;
	showTutorialModal: () => void;
	showPredictionShareModal: () => void;

	get rounds(): IRound[];
	get firstRoundId(): number;
	get userName(): string;
	get gameBar(): IGameBarResponse;
	get isShowSocialSharing(): boolean;
}

@injectable()
export class GameBarController implements IGameBarController {
	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.CrunchStore) private _crunchStore: ICrunchStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.RankingsStore) private _rankingsStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	get rounds(): IRound[] {
		return this._roundsStore.roundsForPicks;
	}

	get firstRoundId(): number {
		return this._roundsStore.currentRoundForPrediction?.id || 0;
	}

	get userName(): string {
		return this._userStore.user?.username || "-";
	}

	get gameBar() {
		return this._rankingsStore.gameBarData;
	}

	get isShowSocialSharing() {
		return (
			this._roundsStore.currentSelectedRound?.status !== RoundStatus.Complete &&
			this._predictionsStore.list.length ===
				this._roundsStore.tournamentsBySelectedRound.length &&
			!this._predictionsStore.isChanged
		);
	}

	dispose(): void {
		return;
	}

	@action
	public roundChange = (round: IRound): void => {
		this._roundsStore.setSelectedRound(round);
		void this._predictionsStore.fetchPredictionsTips(round.id);
		const roundId = round.status !== RoundStatus.Scheduled ? round.id : null;
		void this._rankingsStore.fetchGameBar(roundId);

		if (this._userStore.isAuthorized) {
			void this._crunchStore.fetchCrunch(round.id);
			void this._predictionsStore.fetchPredictionsAnswers(round.id);
		}
	};

	@action
	public showTutorialModal = (): void => {
		this._modalsStore.showModal(ModalType.TUTORIAL);
	};

	@action
	public showPredictionShareModal = (): void => {
		this._modalsStore.showModal(ModalType.PREDICTIONSSHARE);
	};

	init(): void {
		const roundId =
			this._roundsStore.currentRoundForPrediction &&
			this._roundsStore.currentRoundForPrediction?.status !== RoundStatus.Scheduled
				? this._roundsStore.currentRoundForPrediction?.id
				: null;
		void this._rankingsStore.fetchGameBar(roundId);
	}
}
